import Phaser from 'phaser';
import { TILE_SIZE, type FarmPlot } from '../data/world-config';
import type { GameScene } from '../scenes/GameScene';

interface ExpansionZone {
  x: number;      // tile x of the expansion marker
  y: number;      // tile y of the expansion marker
  direction: 'north' | 'south' | 'east' | 'west';
  cost: number;
  purchased: boolean;
  marker?: Phaser.GameObjects.Container;
}

export class FarmExpansionSystem {
  private scene: GameScene;
  private expansions: ExpansionZone[] = [];
  private farmOriginX: number;
  private farmOriginY: number;
  private baseSize: number = 8; // current inner plot size
  private currentExpansionLevel: number = 0;

  constructor(scene: GameScene, farmCenterX: number, farmCenterY: number) {
    this.scene = scene;
    this.farmOriginX = Math.floor(farmCenterX / TILE_SIZE) - 8; // farm outer origin
    this.farmOriginY = Math.floor(farmCenterY / TILE_SIZE) - 8;

    this.createExpansionMarkers();
  }

  private createExpansionMarkers(): void {
    const innerOffset = 4; // offset from farm outer origin to inner plots
    const plotEndX = this.farmOriginX + innerOffset + this.baseSize;
    const plotEndY = this.farmOriginY + innerOffset + this.baseSize;
    const plotStartX = this.farmOriginX + innerOffset;
    const plotStartY = this.farmOriginY + innerOffset;

    // Four expansion zones around the current farm
    const zones: Omit<ExpansionZone, 'purchased' | 'marker'>[] = [
      { x: plotStartX + Math.floor(this.baseSize / 2), y: plotStartY - 2, direction: 'north', cost: 30 },
      { x: plotStartX + Math.floor(this.baseSize / 2), y: plotEndY + 1, direction: 'south', cost: 30 },
      { x: plotEndX + 1, y: plotStartY + Math.floor(this.baseSize / 2), direction: 'east', cost: 30 },
      { x: plotStartX - 2, y: plotStartY + Math.floor(this.baseSize / 2), direction: 'west', cost: 30 },
    ];

    for (const zone of zones) {
      const expansion: ExpansionZone = {
        ...zone,
        cost: zone.cost + this.currentExpansionLevel * 15,
        purchased: false,
      };

      this.createMarkerVisual(expansion);
      this.expansions.push(expansion);
    }
  }

  private createMarkerVisual(zone: ExpansionZone): void {
    const px = zone.x * TILE_SIZE + TILE_SIZE / 2;
    const py = zone.y * TILE_SIZE + TILE_SIZE / 2;

    const container = this.scene.add.container(px, py);
    container.setDepth(3);

    // Pulsing expand icon
    const icon = this.scene.add.graphics();
    icon.fillStyle(0x6aaa3a, 0.4);
    icon.fillCircle(0, 0, 6);
    icon.lineStyle(1, 0x6aaa3a, 0.8);
    icon.strokeCircle(0, 0, 6);
    container.add(icon);

    // Plus sign
    const plus = this.scene.add.text(0, 0, '+', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#6aaa3a',
      fontStyle: 'bold',
    });
    plus.setOrigin(0.5, 0.5);
    container.add(plus);

    // Cost label
    const costText = this.scene.add.text(0, 10, `${zone.cost}c`, {
      fontFamily: 'monospace',
      fontSize: '6px',
      color: '#e8a038',
    });
    costText.setOrigin(0.5, 0);
    container.add(costText);

    // Pulse animation
    this.scene.tweens.add({
      targets: icon,
      alpha: 0.2,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    zone.marker = container;
  }

  tryExpand(tileX: number, tileY: number): boolean {
    for (const zone of this.expansions) {
      if (zone.purchased) continue;

      // Check if player is clicking near the expansion marker
      const dx = Math.abs(tileX - zone.x);
      const dy = Math.abs(tileY - zone.y);
      if (dx <= 1 && dy <= 1) {
        return this.purchaseExpansion(zone);
      }
    }
    return false;
  }

  private purchaseExpansion(zone: ExpansionZone): boolean {
    if (!this.scene.combatSystem) return false;

    const coins = this.scene.combatSystem.playerCoins;
    if (coins < zone.cost) {
      this.scene.events.emit('notification', `Need ${zone.cost} coins to expand! (Have ${coins})`);
      return true; // consumed the interaction
    }

    this.scene.combatSystem.playerCoins -= zone.cost;
    zone.purchased = true;
    this.currentExpansionLevel++;

    // Remove marker
    zone.marker?.destroy();

    // Add new plots
    this.addExpansionPlots(zone);

    this.scene.events.emit('notification', `Farm expanded ${zone.direction}! +12 plots`);
    this.scene.events.emit('farm-expanded');
    this.scene.events.emit('combat-stats-update', this.scene.combatSystem.getStats());

    return true;
  }

  private addExpansionPlots(zone: ExpansionZone): void {
    const farmSystem = this.scene.farmSystem;
    const plots = farmSystem.getPlots();
    const plotSprites = farmSystem['plotSprites'] as Map<string, Phaser.GameObjects.Sprite>;

    // Determine the new plot area (4x3 or 3x4 strip)
    let startX: number, startY: number, w: number, h: number;

    const innerOffset = 4;
    const plotStartX = this.farmOriginX + innerOffset;
    const plotStartY = this.farmOriginY + innerOffset;

    switch (zone.direction) {
      case 'north':
        startX = plotStartX;
        startY = plotStartY - 2;
        w = this.baseSize;
        h = 2;
        break;
      case 'south':
        startX = plotStartX;
        startY = plotStartY + this.baseSize;
        w = this.baseSize;
        h = 2;
        break;
      case 'east':
        startX = plotStartX + this.baseSize;
        startY = plotStartY;
        w = 2;
        h = this.baseSize;
        break;
      case 'west':
        startX = plotStartX - 2;
        startY = plotStartY;
        w = 2;
        h = this.baseSize;
        break;
    }

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const tileX = startX + x;
        const tileY = startY + y;
        const key = `${tileX},${tileY}`;

        if (plots.has(key)) continue;

        const plot: FarmPlot = {
          x: tileX,
          y: tileY,
          growthStage: 0,
          waterLevel: 0,
        };
        plots.set(key, plot);

        // Create soil sprite with pop-in animation
        const sprite = this.scene.add.sprite(
          tileX * TILE_SIZE + TILE_SIZE / 2,
          tileY * TILE_SIZE + TILE_SIZE / 2,
          'terrain', 24
        );
        sprite.setDepth(1);
        sprite.setScale(0);
        plotSprites.set(key, sprite);

        // Pop-in animation staggered
        this.scene.tweens.add({
          targets: sprite,
          scaleX: 1,
          scaleY: 1,
          duration: 300,
          delay: (x + y) * 50,
          ease: 'Back.easeOut',
        });
      }
    }

    // Add fences around new area
    this.addExpansionFences(startX, startY, w, h);
  }

  private addExpansionFences(startX: number, startY: number, w: number, h: number): void {
    // Simple fence border around new plots
    for (let i = -1; i <= w; i++) {
      // Top
      this.scene.add.sprite(
        (startX + i) * TILE_SIZE + TILE_SIZE / 2,
        (startY - 1) * TILE_SIZE + TILE_SIZE / 2,
        'terrain', 28
      ).setDepth(2);
      // Bottom
      this.scene.add.sprite(
        (startX + i) * TILE_SIZE + TILE_SIZE / 2,
        (startY + h) * TILE_SIZE + TILE_SIZE / 2,
        'terrain', 28
      ).setDepth(2);
    }
    for (let i = 0; i < h; i++) {
      // Left
      this.scene.add.sprite(
        (startX - 1) * TILE_SIZE + TILE_SIZE / 2,
        (startY + i) * TILE_SIZE + TILE_SIZE / 2,
        'terrain', 29
      ).setDepth(2);
      // Right
      this.scene.add.sprite(
        (startX + w) * TILE_SIZE + TILE_SIZE / 2,
        (startY + i) * TILE_SIZE + TILE_SIZE / 2,
        'terrain', 29
      ).setDepth(2);
    }
  }
}
